import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import api from "../api";
import AuthLayout from "./AuthLayout";

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [challenge, setChallenge] = useState(null);
  const [code, setCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [notice, setNotice] = useState('');
  useEffect(() => {
    if (!cooldown) return;
    const timer = setTimeout(() => setCooldown(value => Math.max(0, value - 1)), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const nextPath = useMemo(
    () => searchParams.get("next") || location.state?.nextPath || "",
    [location.state?.nextPath, searchParams]
  );
  const loginNotice = location.state?.loginNotice || "";

  const assignSession = (user, token) => {
    localStorage.setItem("token", token);
    localStorage.setItem("email", user.email);
    localStorage.setItem("username", user.name || user.email || "Tenant");
    localStorage.setItem("role", user.role || "tenant");
    localStorage.setItem("loggedIn", "true");
    if ((user.role || "tenant") === "tenant") {
      localStorage.setItem("tenant_has_linked_unit", "false");
    }
    if (onLogin) {
      onLogin(user.role || "tenant");
    }
  };

  const redirectAfterLogin = (role) => {
    if (role === "admin") {
      navigate("/home");
      return;
    }
    navigate(nextPath || "/tenant/home");
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    setError("");

    if (!challenge && (!email || !password)) {
      setError("Please enter both email and password.");
      return;
    }

    setLoading(true);
    try {
      const response = challenge
        ? await api.post('/login/verify', { challenge_id: challenge, code, trust_device: trustDevice }, { withCredentials: true })
        : await api.post("/login", { email, password }, { withCredentials: true });
      const data = response.data || {};
      if (data.status === 'verification_required') {
        setChallenge(data.challenge_id);
        setPassword('');
        setCooldown(data.resend_after || 60);
        setNotice(data.message);
        return;
      }
      const user = data.data?.user || data.user;
      const token = data.data?.token || data.token;

      if (!user || !token) {
        throw new Error("Invalid login response.");
      }

      assignSession(user, token);
      redirectAfterLogin(user.role || "tenant");
    } catch (apiError) {
      const serverText =
        typeof apiError.response?.data === "string" ? apiError.response.data.toLowerCase() : "";
      if (apiError.response?.status === 401) {
        setError("Incorrect email or password.");
      } else if (serverText.includes("redirecting to") || apiError.response?.status === 302) {
        setError("Sign-in is temporarily unavailable. Please try again later.");
      } else if (apiError.response?.data?.message) {
        setError(apiError.response.data.message);
      } else {
        setError("We couldn’t connect. Please check your connection and try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.post('/login/resend', { challenge_id: challenge }, { withCredentials: true });
      setNotice(response.data.message);
      setCode('');
      setCooldown(response.data.resend_after || 60);
    } catch (failure) {
      setError(failure.response?.data?.message || 'Unable to resend. Please try again.');
    } finally { setLoading(false); }
  };

  return (
    <AuthLayout title="Good to have you back." description="Pick up where you left off. Your rental journey and property workspace are right here.">
      <p className="ws-eyebrow">YOUR RENTAL WORKSPACE</p>
      <h1 className="auth-title">{challenge ? 'Check your email.' : 'Welcome back.'}</h1>
      <p className="auth-subtitle">{challenge ? `Enter the six-digit code sent to ${email}.` : 'Sign in to your Vergara Rentals account.'}</p>
      <form onSubmit={handleLogin} className="auth-login-form">
        {loginNotice && <p className="ws-notice" role="status">{loginNotice}</p>}
        {challenge ? <>
          {notice && <p className="ws-notice" role="status">{notice}</p>}
          <label htmlFor="login-code">Verification code</label>
          <input id="login-code" autoFocus inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} value={code} onChange={event => setCode(event.target.value.replace(/\D/g, ''))} required />
          <label style={{ display: 'flex', alignItems: 'center', gap: 10 }}><input style={{ width: 'auto' }} type="checkbox" checked={trustDevice} onChange={event => setTrustDevice(event.target.checked)} />Trust this device for 30 days</label>
          <p className="auth-subtitle">Use this option only on your personal device.</p>
        </> : <><label htmlFor="login-email">Email address</label>
        <input id="login-email" type="email" autoComplete="username" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="you@example.com" required />
        <div className="auth-password-label"><label htmlFor="login-password">Password</label><Link to="/forgot-password">Forgot password?</Link></div>
        <div className="auth-password-input"><input id="login-password" type={showPassword ? "text" : "password"} autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder="Enter your password" required /><button type="button" aria-label={showPassword ? "Hide password" : "Show password"} aria-pressed={showPassword} onClick={() => setShowPassword((value) => !value)}>{showPassword ? "Hide" : "Show"}</button></div>
        </>}
        {error && <p className="ws-error" role="alert">{error}</p>}
        <button className="ws-button auth-submit" type="submit" disabled={loading}>{loading ? "Please wait…" : challenge ? 'Verify and sign in' : "Sign in"} <span aria-hidden="true">↗</span></button>
        {challenge && <><button type="button" className="ws-button ws-button-secondary" disabled={loading || cooldown > 0} onClick={resend}>{cooldown ? `Resend in ${cooldown}s` : 'Resend code'}</button><button type="button" className="ws-button ws-button-secondary" disabled={loading} onClick={() => { setChallenge(null); setCode(''); setError(''); setNotice(''); setTrustDevice(false); }}>Back to sign in</button></>}
      </form>
      <p className="auth-alternate">New to Vergara Rentals? <Link to={nextPath ? `/signup?next=${encodeURIComponent(nextPath)}` : "/signup"}>Create an account</Link></p>
      <div className="auth-form-note">One account. Everything you need for your rental.</div>
    </AuthLayout>
  );
}
export default Login;
